import {
  type ChatTool,
  type ToolResult,
  toolResultFailed,
  type ToolUseProps,
} from "../components/Chat/ChatTool";
import {
  CommandRun,
  jsonRecord,
  jsonString,
  rawOutput,
} from "../components/Chat/CommandRun";
import { Step } from "../components/Step";
import { bashActivityIcon } from "./bashActivityIcon";

export type BashData = {
  getStepColor: (stepName: string) => string | undefined;
};

function BashHeader({ toolUse, result, data, interrupted }: ToolUseProps) {
  const input = jsonRecord(toolUse.input);
  const activeDescription = jsonString(input?.activeDescription);
  const completedDescription = jsonString(input?.completedDescription);
  const failedDescription = jsonString(input?.failedDescription);
  const stepName = jsonString(input?.step);
  const color = stepName
    ? (data as BashData | undefined)?.getStepColor(stepName)
    : undefined;

  return (
    <Step
      color={color ? `var(--${color}-600)` : undefined}
      icon={bashActivityIcon(activeDescription ?? completedDescription)}
      name={stepName}
      done={!!result}
      interrupted={interrupted}
      pastParticiple={completedDescription ?? "Done"}
      inProgress={activeDescription ? `${activeDescription}…` : "Working…"}
      failure={
        result && failed(result) ? (failedDescription ?? "Failed") : undefined
      }
    />
  );
}

function failed(result: ToolResult) {
  const exitCode = jsonRecord(result.output)?.exitCode;
  return toolResultFailed(result) || (exitCode !== undefined && exitCode !== 0);
}

function outputOf(result: ToolResult) {
  const output = jsonRecord(result.output);
  if (output && "exitCode" in output) {
    return {
      stdout: jsonString(output.stdout),
      stderr: jsonString(output.stderr),
      notes: Array.isArray(output.changes)
        ? output.changes.filter((c) => typeof c === "string")
        : [],
      failure:
        output.exitCode === 0 ? undefined : `Exit code ${output.exitCode}`,
    };
  }
  return rawOutput(result);
}

function BashBody({ toolUse, result }: ToolUseProps) {
  const command = jsonString(jsonRecord(toolUse.input)?.command);

  return (
    <CommandRun
      sources={command ? [{ language: "shell", code: command }] : []}
      {...(result ? outputOf(result) : {})}
    />
  );
}

export const bash = (data: BashData): ChatTool => ({
  name: "bash",
  Header: BashHeader,
  Body: BashBody,
  PendingBody: BashBody,
  data,
});
